import { type ReactNode } from "react";
import { StatusBadge } from "../../components/index.ts";
import type { LifecyclePage } from "./use-identity.ts";

/**
 * The epochs panel (task 11.7; design 12.2 Identity "key and revocation epochs"; 12.3 "unverifiable
 * never green"; 12.4 status by icon + text).
 *
 * The lifecycle page carries the CURRENT key and revocation epochs alongside the timeline. Each is a
 * monotonically-increasing counter the control plane bumps when it rotates signing keys or advances
 * the revocation generation; a client that pins an older epoch must refresh. The panel shows both
 * as plain numbers.
 *
 * Honesty (design 12.3): a `null` epoch means the control plane could not report it, which is NOT a
 * healthy state and must never read green — it is shown with a grey `unknown` badge and an explicit
 * "unknown" value, distinct from a real epoch of 0.
 */

export interface EpochsPanelProps {
  readonly epochs: LifecyclePage["epochs"];
}

export function EpochsPanel(props: EpochsPanelProps): ReactNode {
  const { epochs } = props;
  return (
    <section className="idn-epochs" aria-label="Key and revocation epochs">
      <div className="idn-section__head">
        <h2 className="idn-section__title">Epochs</h2>
        <span className="idn-section__note">current signing-key and revocation generations</span>
      </div>
      <dl className="idn-epochs__grid">
        <EpochTile label="Key epoch" value={epochs.key_epoch} />
        <EpochTile label="Revocation epoch" value={epochs.revocation_epoch} />
      </dl>
    </section>
  );
}

/** A single epoch tile. A null value is grey `unknown` (never green) with an "unknown" reading. */
function EpochTile(props: { label: string; value: number | null }): ReactNode {
  const known = props.value !== null;
  return (
    <div className="idn-epoch" data-measure={known ? "exact" : "unverifiable"}>
      <dt className="idn-epoch__label">{props.label}</dt>
      <dd className="idn-epoch__value">
        {known ? (
          <span className="idn-num">{props.value}</span>
        ) : (
          <StatusBadge
            level="unknown"
            label="Unknown"
            size="sm"
            title={`The control plane did not report the ${props.label.toLowerCase()}.`}
          />
        )}
      </dd>
    </div>
  );
}
